import { Component, Input, OnInit } from '@angular/core';
import { QualityRepoService } from '@client/app/services/api/quality-repo/quality-repo.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { SessionService } from '@monsido/core/session/session.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { ChangesHistoryService } from '@client/app/pages/global/changes-history/services/changes-history/changes-history.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';

@Component({
    selector: 'domain-dashboard-qa',
    standalone: false,
    template: `
        <div class="card panel-monsido-card max-height d-flex flex-column">
            <a class="p-0" [uiSref]="'base.customer.domain.qa.summary'" [attr.aria-label]="'QA' | translate">
                <mon-panel-header [pHeader]="'Content Quality' | translate">
                    <div class="text-right">
                        <div class="btn btn-link">
                            <span class="pl-1 fas fa-chevron-right" aria-hidden="true"></span>
                        </div>
                    </div>
                </mon-panel-header>
            </a>
            <div class="card-body flex-grow-1">
                <mon-spinner [loading]="loading">
                    <div class="row mt-3">
                        <div class="offset-4 col-18">
                            <mon-chart-doughnut [colors]="doughnutColors" [labels]="doughnutLabels" [data]="doughnutData" [options]="doughnutOptions">
                            </mon-chart-doughnut>
                        </div>
                        <div class="col-26">
                            <div class="row">
                                <div class="vcenter col-48 mb-4 px-0">
                                    <notification-change [title]="'QA Issues' | translate"
                                                         [digit]="latestQaErrors"
                                                         [miniDigits]="changesSinceLastScan"
                                                         link="base.customer.domain.qa.summary"
                                                         unit="">
                                        <span translate>Affects</span>
                                        <span class="font-weight-500 text-black outer-l-smurf outer-r-smurf">
                                            {{pages_count | monNumber:'0,0'}}
                                        </span>
                                        <span translate>pages</span>
                                        <div *ngIf="domain.scan.scan_documents">
                                            <span translate>and</span>
                                            <span class="font-weight-500 text-black outer-l-smurf outer-r-smurf">
                                                {{documents_count | monNumber:'0,0'}}
                                            </span>
                                            <span translate>documents</span>
                                        </div>
                                    </notification-change>
                                </div>
                                <div class="col-48 px-0">
                                    <ul class="list-inline mb-0">
                                        <li [ngbTooltip]="'Broken Links' | translate" [triggers]="'focus hover'">
                                            <a [uiSref]="'base.customer.domain.qa.broken.links'"
                                               class="text-black"
                                               [attr.aria-label]="'Go to broken Links' | translate">
                                                <span class="fas fa-unlink fa-lg outer-l-small outer-r-smurf"
                                                      aria-hidden="true"
                                                      [ngClass]="{'text-danger': broken_links > 0, 'text-grey': broken_links === 0}">
                                                </span>
                                                <span class="sr-only" translate>Broken Links</span>
                                                <span>
                                                    {{broken_links | monNumber}}
                                                </span>
                                            </a>
                                        </li>
                                        <li [ngbTooltip]="'Broken images' | translate" [triggers]="'focus hover'">
                                            <a [uiSref]="'base.customer.domain.qa.broken.images'" class="text-black" [attr.aria-label]="'Go to broken images' | translate">
                                                <span class="fas fa-file-image fa-lg outer-r-smurf"
                                                      aria-hidden="true"
                                                      [ngClass]="{'text-danger': broken_images > 0, 'text-grey': broken_images === 0}">
                                                </span>
                                                <span class="sr-only" translate>Broken Images</span>
                                                <span>
                                                    {{broken_images | monNumber}}
                                                </span>
                                            </a>
                                        </li>
                                        <li [ngbTooltip]="'Confirmed misspellings' | translate" [triggers]="'focus hover'">
                                            <a [uiSref]="'base.customer.domain.qa.spellcheck.confirmed'"
                                               class="text-black"
                                               [attr.aria-label]="'Go to mispellings' | translate">
                                                <span class="fas fa-language fa-lg outer-r-smurf"
                                                      aria-hidden="true"
                                                      [ngClass]="{'text-confirmed-misspelling-color': misspellings > 0, 'text-grey': misspellings === 0}">
                                                </span>
                                                <span class="sr-only" translate>Misspellings</span>
                                                <span>
                                                    {{misspellings | monNumber}}
                                                </span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </mon-spinner>
            </div>
            <div class="card-footer bg-white text-right" *ngIf="showHistory">
                <a class="btn-link" [uiSref]="'base.customer.domain.changesHistory'" [uiParams]="historyCenterParameters" translate>
                    Show history
                </a>
            </div>
        </div>
    `,
})
export class DomainDashboardQaComponent implements OnInit {
    @Input() historyData: any = [];

    historyCenterParameters: any = {};
    loading: boolean = true;
    domain: any = {};
    latestCrawl: any = {};
    showHistory: boolean = false;
    doughnutLabels: any[] = [[''], ['']];
    doughnutColors: string[] = ['#4dc0bd', '#DCDCDC'];
    doughnutData: number[] = [];
    doughnutOptions: any = {};
    total: number = 0;
    totalPages: number = 0;
    total_pages_count: number = 0;
    total_documents_count: number = 0;
    pages_count: number = 0;
    documents_count: number = 0;
    percentage: number = 0;
    broken_links: number = 0;
    broken_images: number = 0;
    misspellings: number = 0;
    latestQaErrors: number = 0;
    changesSinceLastScan: any;
    promise: any;

    constructor (
        private qualityRepoService: QualityRepoService,
        private pipeService: PipeService,
        private sessionService: SessionService,
        private translateService: TranslateService,
        private changesHistoryService: ChangesHistoryService,
        private activeFeatureService: ActiveFeatureService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    private activate (): void {
        this.historyCenterParameters = this.changesHistoryService.getHistoryCenterParameters('qa');
        this.loading = true;
        this.domain = this.sessionService.domain;
        this.latestCrawl = this.getLatestHistory();
        this.showHistory = this.activeFeatureService.isFeatureActivePure('history_center_all');
        this.setupDoughnutChart();
    }

    private getLatestHistory (): any {
        if (this.historyData.length > 0) {
            return this.historyData.sort((a: any, b: any) => {
                // Get the latest history id
                return a.id > b.id ? -1 : a.id < b.id ? 1 : 0;
            })[0];
        } else {
            return {};
        }
    }

    private setupDoughnutChart (): void {
        this.doughnutLabels = [[''], ['']];
        this.doughnutColors = ['#4dc0bd', '#DCDCDC'];
        this.total = 0;
        this.totalPages = 0;
        this.promise = this.qualityRepoService.getSummary({}).then((data: any) => {
            this.total_pages_count = data.pages_with_quality_notifications + data.pages_without_quality_notifications;
            this.total_documents_count = data.documents_with_quality_notifications + data.documents_without_quality_notifications;
            this.totalPages = this.total_pages_count + this.total_documents_count;

            this.pages_count = data.pages_with_quality_notifications;
            this.documents_count = data.documents_with_quality_notifications;

            this.total = this.pages_count + this.documents_count;
            this.percentage = (this.total / this.totalPages) * 100;

            this.doughnutData = [this.totalPages - this.total, this.total];

            this.broken_links = data.broken_links;
            this.broken_images = data.broken_images;
            this.misspellings = data.confirmed_misspellings;

            this.doughnutOptions = {
                centerText: {
                    percentageLabel: this.pipeService.get('monNumber')((100 - this.percentage) / 100, '0.[00] %'),
                    totalLabel: this.translateService.getString('overall compliance'),
                    subLabel: this.translateService.getString('achieved'),
                    totalFontSize: 150,
                    subFontSize: 150,
                },
                cutoutPercentage: 80,
                tooltips: {
                    enabled: false,
                },
                hover: {
                    mode: null,
                },
            };

            this.latestQaErrors = data.broken_links + data.broken_images + data.confirmed_misspellings;
            this.loading = false;
        }, () => {});
    }
}
